import { BackgroundJobStatus } from '@models/backgroundJobs/IBackgroundJob';

/**
 * Что колокольчик играет, когда фоновая задача закончилась:
 * - `success` / `failure` / `cancel` — финал последней задачи: кольцо замыкается цветом итога и гаснет;
 * - `flash-success` / `flash-failure` — другие задачи ещё идут: вспышка цвета итога на вращающемся кольце;
 * - `defer` — вкладка скрыта: решить заново, когда она станет видимой;
 * - `none` — молчать: итог уже перед глазами.
 *
 * `arrive` — не итог: задача ушла в фон и прилетела в колокольчик (`flyToBell`) — вспышка кольца и мягкий взмах.
 */
export type BellJobFx =
    'none' | 'defer' | 'success' | 'failure' | 'cancel' | 'flash-success' | 'flash-failure' | 'arrive';

export type BellJobFxPlayable = Exclude<BellJobFx, 'none' | 'defer'>;

export interface IBellJobFxInput {
    status: BackgroundJobStatus;
    /** Задачу показывает её окно. */
    observed: boolean;
    /** Итог уже показан: окном задачи, тостом или превью уведомления. */
    shown: boolean;
    /** Открыт дропдаун колокольчика — итог видно строкой раздела задач. */
    dropdownOpen: boolean;
    tabVisible: boolean;
    /** Кроме этой идут и другие задачи. */
    othersRunning: boolean;
}

export const decideBellJobFx = ({
    status,
    observed,
    shown,
    dropdownOpen,
    tabVisible,
    othersRunning,
}: IBellJobFxInput): BellJobFx => {
    if (status !== 'succeeded' && status !== 'failed' && status !== 'cancelled') return 'none';
    if (observed || shown || dropdownOpen) return 'none';
    if (!tabVisible) return 'defer';
    if (othersRunning) {
        if (status === 'succeeded') return 'flash-success';
        if (status === 'failed') return 'flash-failure';
        return 'none';
    }
    if (status === 'succeeded') return 'success';
    if (status === 'failed') return 'failure';
    return 'cancel';
};

/** Ошибку, которую пользователь не видел, кнопка помнит меткой, пока колокольчик не откроют. */
export const leavesFailureMark = (fx: BellJobFx): boolean => fx === 'failure' || fx === 'flash-failure';

export const isBellJobFinale = (fx: BellJobFx): fx is 'success' | 'failure' | 'cancel' =>
    fx === 'success' || fx === 'failure' || fx === 'cancel';

/** Длительность сцены, мс: с запасом к самой долгой анимации её классов в `NotificationBell.scss`. */
export const BELL_JOB_FX_MS: Record<BellJobFxPlayable, number> = {
    success: 1800,
    failure: 1600,
    cancel: 900,
    'flash-success': 1000,
    'flash-failure': 1000,
    arrive: 1000,
};

/**
 * Сцена ошибки не уступает успеху или отмене, пришедшим, пока она играет (две задачи закончились разом); сцена итога
 * не уступает прилёту новой задачи.
 */
export const outranksBellJobFx = (playing: BellJobFxPlayable, next: BellJobFxPlayable): boolean =>
    (playing === 'failure' && next !== 'failure') ||
    (playing === 'flash-failure' && next === 'flash-success') ||
    (next === 'arrive' && playing !== 'arrive');
